// ── ADMIN: SETTINGS (general settings + program catalog) ──────────────
// esc (shared classic script) stays a bare global — not migrated yet.
// currencyFocus/currencyBlur (admin-student-actions.js) are a real import
// now — the late-fee field's focus/blur are wired in VIEW_INIT below.
//
// VIEW_INIT['a-settings'] used to live in core/routing.js — relocated here so this
// view's init logic lives with the view itself, same as every other domain
// in this migration; core/routing.js shouldn't have to import view-specific
// logic. renderProgramList()'s Edit buttons are wired by wireProgramList(),
// called both from VIEW_INIT and directly inside saveProgram()'s own
// #programList rewrite (bypasses route()/VIEW_INIT, same shape as
// catalog-shared.js's wireCatalogGroup()). programModal()'s own Save button
// is attached directly inline (imperative function with control over the
// modal DOM it just wrote via openModal).
import { apiFetch } from '../core/api.js';
import { DB, PROGRAMS, currentView, setSettingsDirty } from '../core/state.js';
import { priorityWindowOpen, openEnrollmentOpen } from '../core/domain-utils.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, refresh, sectionTabs } from '../core/routing.js';
import { readMoney, moneyValue } from '../core/format.js';
import { tvWindowOpen } from './catalog-shared.js';
import { priceLabel } from './billing-shared.js';
import { currencyFocus, currencyBlur } from './admin-student-actions.js';

// The API returns "YYYY-MM-DD HH:MM:SS"; <input type="datetime-local"> wants
// "YYYY-MM-DDTHH:MM". Same normalisation admin-store.js already uses for the
// store deadline.
const dtLocal = v => (v ? String(v).replace(' ', 'T').slice(0, 16) : '');


VIEWS['a-settings'] = () => {
  const s = DB.settings;
  return `
    <div class="page-head">
      <h2 class="serif">General <em>Settings</em></h2>
    </div>
    ${sectionTabs('a-settings')}
    <div class="grid-2">
      <div>
        <div class="card">
          <div class="card-hd">Registration</div>
          <div class="field"><label>Upcoming season</label>
            <input type="number" id="stSeasonYear" value="${s.upcomingSeasonYear}" min="2024" max="2099">
          </div>
          <div class="field" style="margin-top:14px">
            <label style="display:flex;justify-content:space-between;align-items:center">
              <span>Priority window opens <span style="font-size:10px;color:var(--muted);text-transform:none;letter-spacing:0">(returning families)</span></span>
              <span style="font-size:11px;text-transform:none;letter-spacing:0;color:${priorityWindowOpen() ? '#34d399' : 'var(--red)'}">${priorityWindowOpen() ? '● Currently open' : '○ Closed'}</span>
            </label>
            <input type="datetime-local" id="stPriorityOpens" value="${dtLocal(s.priorityOpensAt)}">
            <label style="margin-top:10px">Re-enrollment deadline <span style="font-size:10px;color:var(--muted);text-transform:none;letter-spacing:0">(window closes; blank = no deadline)</span></label>
            <input type="datetime-local" id="stPriorityCloses" value="${s.priorityClosesAt ? dtLocal(s.priorityClosesAt) : ''}">
            <div style="display:flex;align-items:center;gap:8px;margin-top:8px">
              <input type="checkbox" id="stPriorityClosed" style="width:auto" ${s.priorityManualClosed ? 'checked' : ''}>
              <label style="text-transform:none;letter-spacing:0;font-size:12px;color:var(--cream)">Stop accepting registrations</label>
            </div>
          </div>
          <div class="field" style="margin-top:14px">
            <label style="display:flex;justify-content:space-between;align-items:center">
              <span>Open enrollment opens <span style="font-size:10px;color:var(--muted);text-transform:none;letter-spacing:0">(new families)</span></span>
              <span style="font-size:11px;text-transform:none;letter-spacing:0;color:${openEnrollmentOpen() ? '#34d399' : 'var(--red)'}">${openEnrollmentOpen() ? '● Currently open' : '○ Closed'}</span>
            </label>
            <input type="datetime-local" id="stOpenEnrollOpens" value="${dtLocal(s.openEnrollmentOpensAt)}">
            <label style="margin-top:10px">Registration closes <span style="font-size:10px;color:var(--muted);text-transform:none;letter-spacing:0">(new-family form; blank = no close date)</span></label>
            <input type="datetime-local" id="stOpenEnrollCloses" value="${s.registrationClosesAt ? dtLocal(s.registrationClosesAt) : ''}">
            <div style="display:flex;align-items:center;gap:8px;margin-top:8px">
              <input type="checkbox" id="stOpenEnrollClosed" style="width:auto" ${s.openEnrollmentManualClosed ? 'checked' : ''}>
              <label style="text-transform:none;letter-spacing:0;font-size:12px;color:var(--cream)">Stop accepting registrations</label>
            </div>
          </div>
          <div style="margin-top:20px;padding-top:18px;border-top:1px solid var(--border-soft)">
            <div style="font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:var(--muted);margin-bottom:4px">Firma contract templates</div>
            <p style="font-size:12px;color:var(--muted);margin-bottom:12px">Template IDs from your Firma account — one per program. Sent automatically when a performer is approved.</p>
            ${PROGRAMS.filter(p => p.isProgram).map(p => `
              <div class="field" style="margin-top:10px"><label>${esc(p.name)} template ID</label>
                <input type="text" id="stTpl_${p.id}" value="${esc((s.firmaTemplates || {})[p.id] || '')}" placeholder="e.g. 72021b0c-..." spellcheck="false"></div>
            `).join('')}
          </div>
          <div style="margin-top:20px;padding-top:18px;border-top:1px solid var(--border-soft)">
            <div style="font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:var(--muted);margin-bottom:4px">Rehearsal schedule links</div>
            <p style="font-size:12px;color:var(--muted);margin-bottom:12px">Links to rehearsal schedules (e.g. Google Drive). Sent in the approval email, and shown on the dashboard of every family enrolled in that program. Make sure the document is shared with <em>Anyone with the link</em> as a <em>Viewer</em>.</p>
            ${PROGRAMS.filter(p => p.isProgram).map(p => `
              <div class="field" style="margin-top:10px"><label>${esc(p.name)} schedule URL</label>
                <input type="url" id="stSchedule_${p.id}" value="${esc((s.rehearsalScheduleUrls || {})[p.id] || '')}" placeholder="https://drive.google.com/..." spellcheck="false"></div>
            `).join('')}
          </div>
          <div style="margin-top:20px;padding-top:18px;border-top:1px solid var(--border-soft)">
            <div style="font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:var(--muted);margin-bottom:4px">Parent meeting details</div>
            <p style="font-size:12px;color:var(--muted);margin-bottom:12px">Included in the registration review email. Example: "Sept. 9 at 4:00 PM (about 45 minutes)"</p>
            ${PROGRAMS.filter(p => p.isProgram).map(p => `
              <div class="field" style="margin-top:10px"><label>${esc(p.name)} parent meeting</label>
                <input type="text" id="stMeeting_${p.id}" value="${esc((s.parentMeetingInfo || {})[p.id] || '')}" placeholder="e.g. Sept. 9 at 4:00 PM (about 45 minutes)"></div>
            `).join('')}
          </div>
          <button class="btn-ghost" data-action="save-registration" style="margin-top:14px">Save registration settings</button>
        </div>
        <div class="card">
          <div class="card-hd">Classes</div>
          <p style="font-size:12px;color:var(--muted);margin-bottom:12px">Separate window from PC/BB above — open to any existing family. Nothing is charged at registration; each class bills monthly (same as PC/BB) for whichever months it's set up to run — see the Classes Catalog to set a class's rate and active months.</p>
          <div class="field">
            <label style="display:flex;justify-content:space-between;align-items:center">
              <span>Registration opens</span>
              <span style="font-size:11px;text-transform:none;letter-spacing:0;color:${tvWindowOpen() ? '#34d399' : 'var(--red)'}">${tvWindowOpen() ? '● Currently open' : '○ Closed'}</span>
            </label>
            <input type="datetime-local" id="stTVOpens" value="${dtLocal(DB.settings.tapVoiceOpensAt)}">
          </div>
          <div class="field" style="margin-top:14px"><label>Registration closes <span class="plain-cap">(optional)</span></label>
            <input type="datetime-local" id="stTVCloses" value="${dtLocal(DB.settings.tapVoiceClosesAt)}">
          </div>
          <div style="display:flex;align-items:center;gap:8px;margin-top:12px">
            <input type="checkbox" id="stTVClosed" style="width:auto" ${s.tapVoiceManualClosed ? 'checked' : ''}>
            <label style="text-transform:none;letter-spacing:0;font-size:12px;color:var(--cream)">Stop accepting registrations</label>
          </div>
          <button class="btn-ghost" data-action="save-tapvoice" style="margin-top:14px">Save Classes settings</button>
        </div>
        <div class="card">
          <div class="card-hd">Billing automation</div>
          <div class="field-row">
            <div class="field"><label>Late fee <span class="plain-cap">(per week)</span></label><input type="text" id="stLateFee" value="${'$' + Number(s.lateFeeAmount).toFixed(2)}" inputmode="decimal"></div>
            <div class="field"><label>Grace period <span class="plain-cap">(days)</span></label><input type="number" id="stGraceDays" value="${s.lateFeeGraceDays}" min="1"></div>
          </div>
          <div class="field-row">
            <div class="field"><label>Reminder email delay <span class="plain-cap">(days)</span></label><input type="number" id="stRetryDays" value="${s.retryEmailDays}" min="1"></div>
            <div class="field"></div>
          </div>
          <button class="btn-ghost" data-action="save-billing">Save billing settings</button>
          <div style="margin-top:16px;padding:12px 14px;background:rgba(255,255,255,0.02);border:1px solid var(--border-soft);border-radius:8px;font-size:12px;color:var(--muted);line-height:1.8">
            <b style="color:var(--cream)">Current flow when a payment fails:</b><br>
            1. Immediate email — subject: <span style="color:var(--cream)">CSYP: Payment failed — action needed</span><br>
            2. Reminder email after ${s.retryEmailDays} ${s.retryEmailDays === 1 ? 'day' : 'days'} — subject: <span style="color:var(--cream)">CSYP: Reminder — payment still due</span><br>
            3. After ${s.lateFeeGraceDays} days, a late fee of ${fmt$(s.lateFeeAmount)} is added each week until the balance is paid<br>
            <span style="display:block;margin-top:8px;padding-top:8px;border-top:1px solid var(--border-soft)">The ladder stops as soon as the balance clears. A parent who saves a new card pays it in that step — the card is charged on save — so a family who acts on the first email never reaches steps 2 or 3.</span>
          </div>
        </div>
      </div>
      <div>
        <div class="card">
          <div class="card-hd">Programs <button class="btn-ghost btn-sm" data-action="add-program">+ Add program</button></div>
          <div id="programList">
            ${renderProgramList()}
          </div>
        </div>
      </div>
    </div>`;
};

// renderProgramList/programModal/saveProgram are Settings-domain
// program-catalog management, distinct from the Summer Workshops/Classes
// Catalog in catalog-shared.js (different screen entirely — see its header
// comment).
function renderProgramList() {
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const list = PROGRAMS.filter(p => p.billingType !== 'workshop'); // workshops have their own catalog screen
  return list.map((p, i) => `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 0;${i < list.length - 1 ? 'border-bottom:1px solid var(--border-soft)' : ''}">
      <div>
        <div style="font-size:14px;color:${p.active === false ? 'var(--muted)' : 'var(--cream)'}">${esc(p.name)}${p.active === false ? ' <span style="font-size:10px;color:var(--muted)">(inactive)</span>' : ''}</div>
        <div style="font-size:12px;color:var(--muted);margin-top:2px">
          ${priceLabel(p)}${p.materialsFee ? ' · ' + fmt$(p.materialsFee) + ' materials' : ''}
          ${p.seasonMonths ? ' · ' + p.seasonMonths.map(m => MONTH_NAMES[m-1]).join(', ') : ' · Year-round'}
          ${p.ageMin !== null || p.ageMax !== null ? ' · Ages ' + (p.ageMin !== null ? p.ageMin + '+' : '') + (p.ageMin !== null && p.ageMax !== null ? ', ' : '') + (p.ageMax !== null ? 'up to ' + p.ageMax : '') : ''}
        </div>
      </div>
      <button class="btn-ghost btn-sm" data-action="edit-program" data-program="${p.id}">Edit</button>
    </div>`).join('');
}
function wireProgramList() {
  document.querySelectorAll('[data-action="edit-program"]').forEach(btn => {
    btn.addEventListener('click', () => programModal(btn.dataset.program));
  });
}
function programModal(programId) {
  const p = programId ? PROGRAMS.find(x => x.id === programId) : null;
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const currentSeason = p && p.seasonMonths ? p.seasonMonths : [];
  openModal(`
    <h3>${p ? 'Edit program' : 'New program'}</h3>
    <div class="field"><label>Program name</label><input type="text" id="pmName" value="${p ? esc(p.name) : ''}" placeholder="e.g. Jazz Dance"></div>
    <div class="field-row">
      <div class="field"><label>Monthly fee</label>
        <div class="money"><span class="cur" aria-hidden="true">$</span><input type="text" inputmode="decimal" id="pmFee" value="${moneyValue(p ? p.monthly : '')}" placeholder="0 for no fee" aria-label="Monthly fee in dollars"></div></div>
      <div class="field"><label>Materials fee</label>
        <div class="money"><span class="cur" aria-hidden="true">$</span><input type="text" inputmode="decimal" id="pmMaterials" value="${p && p.materialsFee ? moneyValue(p.materialsFee) : ''}" placeholder="0 for none" aria-label="Materials fee in dollars"></div></div>
    </div>
    <div class="field">
      <label>Active months <span style="font-size:10px;color:var(--muted);font-weight:400;text-transform:none;letter-spacing:0">(leave all unchecked for year-round)</span></label>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px">
        ${MONTH_NAMES.map((m, i) => {
          const monthNum = i + 1;
          const checked = currentSeason.includes(monthNum);
          return `<label style="display:flex;align-items:center;gap:4px;padding:4px 10px;border-radius:20px;border:1px solid ${checked ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${checked ? 'rgba(201,168,76,0.08)' : 'transparent'};cursor:pointer;font-size:12px;color:${checked ? 'var(--gold-light)' : 'var(--muted)'}">
            <input type="checkbox" name="pmMonth" value="${monthNum}" ${checked ? 'checked' : ''} style="width:auto;accent-color:var(--gold)"> ${m}</label>`;
        }).join('')}
      </div>
    </div>
    <div class="field-row">
      <div class="field"><label>Min age <span class="plain-cap">(returning)</span></label><input type="number" id="pmAgeMin" min="0" max="25" value="${p && p.ageMin !== null ? p.ageMin : ''}" placeholder="e.g. 8"></div>
      <div class="field"><label>Min age <span class="plain-cap">(new families)</span></label><input type="number" id="pmNewAgeMin" min="0" max="25" value="${p && p.newFamilyAgeMin !== null && p.newFamilyAgeMin !== undefined ? p.newFamilyAgeMin : ''}" placeholder="e.g. 9"></div>
    </div>
    <div class="field-row">
      <div class="field"><label>Max age <span class="plain-cap">(blank for none)</span></label><input type="number" id="pmAgeMax" min="0" max="25" value="${p && p.ageMax !== null ? p.ageMax : ''}" placeholder="e.g. 18"></div>
      <div class="field"></div>
    </div>
    ${p ? `<div class="field" style="flex-direction:row;align-items:center;gap:10px;margin-top:4px">
      <input type="checkbox" id="pmActive" style="width:auto" ${p.active !== false ? 'checked' : ''}>
      <label style="text-transform:none;letter-spacing:0;font-size:13px;color:var(--cream)">Active — show in enrollment options</label>
    </div>` : ''}
    <div class="field" style="flex-direction:row;align-items:center;gap:10px;margin-top:4px">
      <input type="checkbox" id="pmIsProgram" style="width:auto" ${p ? (p.isProgram ? 'checked' : '') : ''}>
      <label style="text-transform:none;letter-spacing:0;font-size:13px;color:var(--cream)">This is a Program — shows birthday emails and dashboard widgets</label>
    </div>
    <button class="btn" id="pmSaveBtn" style="margin-top:10px">${p ? 'Save changes' : 'Create program'}</button>`);
  document.getElementById('pmSaveBtn').addEventListener('click', () => saveProgram(p ? p.id : ''));
}
async function saveProgram(programId) {
  const name        = document.getElementById('pmName').value.trim();
  const monthly     = readMoney(document.getElementById('pmFee'));
  const materialsFee = readMoney(document.getElementById('pmMaterials'));
  if (monthly === null) { toastError('Monthly fee must be a dollar amount, like 20 or 20.00'); return; }
  if (materialsFee === null) { toastError('Materials fee must be a dollar amount, like 20 or 20.00'); return; }
  const ageMinRaw   = document.getElementById('pmAgeMin').value.trim();
  const newAgeMinRaw = document.getElementById('pmNewAgeMin').value.trim();
  const ageMaxRaw   = document.getElementById('pmAgeMax').value.trim();
  const ageMin      = ageMinRaw !== '' ? parseInt(ageMinRaw) : null;
  const newFamilyAgeMin = newAgeMinRaw !== '' ? parseInt(newAgeMinRaw) : null;
  const ageMax      = ageMaxRaw !== '' ? parseInt(ageMaxRaw) : null;
  const isProgram   = !!(document.getElementById('pmIsProgram')?.checked);
  const monthChecks = [...document.querySelectorAll('input[name="pmMonth"]:checked')].map(cb => parseInt(cb.value));
  const seasonMonths = monthChecks.length ? monthChecks : null;
  if (!name) { toast('Program name is required'); return; }
  const payload = { name, monthly, materialsFee, isProgram, ageMin, newFamilyAgeMin, ageMax, seasonMonths };
  try {
    if (programId) {
      const activeEl = document.getElementById('pmActive');
      payload.active = activeEl ? activeEl.checked : true;
      const updated = await apiFetch('/programs/' + programId, { method: 'PUT', body: JSON.stringify(payload) });
      const idx = PROGRAMS.findIndex(x => x.id === programId);
      if (idx >= 0) PROGRAMS[idx] = updated;
    } else {
      const created = await apiFetch('/programs', { method: 'POST', body: JSON.stringify(payload) });
      PROGRAMS.push(created);
    }
    closeModal();
    if (currentView === 'a-settings') {
      const listEl = document.getElementById('programList');
      if (listEl) listEl.innerHTML = renderProgramList();
      wireProgramList();
    } else refresh();
    toast(programId ? 'Program updated' : name + ' added');
  } catch (e) { toastError(e.message); }
}

async function saveRegistrationSettings() {
  const s = DB.settings;
  const payload = {
    upcomingSeasonYear: parseInt(document.getElementById('stSeasonYear').value) || s.upcomingSeasonYear,
    priorityOpensAt: (document.getElementById('stPriorityOpens') || {}).value || s.priorityOpensAt,
    priorityClosesAt: document.getElementById('stPriorityCloses') ? (document.getElementById('stPriorityCloses').value || null) : s.priorityClosesAt,
    openEnrollmentOpensAt: (document.getElementById('stOpenEnrollOpens') || {}).value || s.openEnrollmentOpensAt,
    registrationClosesAt: document.getElementById('stOpenEnrollCloses') ? (document.getElementById('stOpenEnrollCloses').value || null) : s.registrationClosesAt,
    priorityManualClosed: document.getElementById('stPriorityClosed').checked,
    openEnrollmentManualClosed: document.getElementById('stOpenEnrollClosed').checked,
    firmaTemplates: {},
    rehearsalScheduleUrls: {},
    parentMeetingInfo: {},
  };
  PROGRAMS.filter(p => p.isProgram).forEach(p => {
    const el = document.getElementById('stTpl_' + p.id);
    if (el) payload.firmaTemplates[p.id] = el.value.trim();
    const schedEl = document.getElementById('stSchedule_' + p.id);
    if (schedEl) payload.rehearsalScheduleUrls[p.id] = schedEl.value.trim();
    const meetEl = document.getElementById('stMeeting_' + p.id);
    if (meetEl) payload.parentMeetingInfo[p.id] = meetEl.value.trim();
  });
  try {
    await apiFetch('/settings/registration', { method: 'PUT', body: JSON.stringify(payload) });
    Object.assign(s, payload);
    setSettingsDirty(false);
    refresh();
    toast('Registration settings saved');
  } catch (e) { toastError(e.message); }
}
async function saveTapVoiceSettings() {
  const payload = {
    tapVoiceOpensAt: (document.getElementById('stTVOpens') || {}).value || DB.settings.tapVoiceOpensAt,
    tapVoiceClosesAt: (document.getElementById('stTVCloses') || {}).value || null,
    tapVoiceManualClosed: document.getElementById('stTVClosed').checked,
  };
  try {
    await apiFetch('/settings/tap-voice', { method: 'PUT', body: JSON.stringify(payload) });
    Object.assign(DB.settings, payload);
    setSettingsDirty(false);
    refresh();
    toast('Classes settings saved');
  } catch (e) { toastError(e.message); }
}
async function saveBillingSettings() {
  const payload = {
    lateFeeAmount: parseFloat((document.getElementById('stLateFee').value || '').replace(/[^0-9.]/g, '')) || 5,
    lateFeeGraceDays: parseInt(document.getElementById('stGraceDays').value) || 7,
    retryEmailDays: parseInt(document.getElementById('stRetryDays').value) || 3,
  };
  try {
    await apiFetch('/settings/billing', { method: 'PUT', body: JSON.stringify(payload) });
    Object.assign(DB.settings, payload);
    setSettingsDirty(false);
    refresh(); toast('Billing settings saved');
  } catch (e) { toastError(e.message); }
}

VIEW_INIT['a-settings'] = () => {
  document.querySelectorAll('[data-action="save-registration"]').forEach(btn => {
    btn.addEventListener('click', saveRegistrationSettings);
  });
  document.querySelectorAll('[data-action="save-tapvoice"]').forEach(btn => {
    btn.addEventListener('click', saveTapVoiceSettings);
  });
  document.querySelectorAll('[data-action="save-billing"]').forEach(btn => {
    btn.addEventListener('click', saveBillingSettings);
  });
  document.querySelectorAll('[data-action="add-program"]').forEach(btn => {
    btn.addEventListener('click', () => programModal());
  });
  const lateFeeEl = document.getElementById('stLateFee');
  if (lateFeeEl) {
    lateFeeEl.addEventListener('focus', () => currencyFocus(lateFeeEl));
    lateFeeEl.addEventListener('blur', () => currencyBlur(lateFeeEl));
  }
  wireProgramList();
};
